import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useQueryStates, parseAsInteger, parseAsBoolean } from 'nuqs'
import { useBillRatesQuery } from 'queries/billRates/useBillRatesQuery'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'

type BillRatesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useBillRatesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    companyId: number | null
    positionId: number | null
    includeDeleted: boolean
    pagination: {
      pageIndex: number
      pageSize: number
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setCompanyId: (value: number | undefined) => void
    setPositionId: (value: number | undefined) => void
    setIncludeDeleted: (value: boolean) => void
  }
}

const [Context, useBillRatesContextValue] =
  createContext<BillRatesContextState>({
    name: 'BillRatesContext',
  })

export const BillRatesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [pagination, setPagination] = usePaginationQueryState()
  const [filters, setFilters] = useQueryStates(
    {
      companyId: parseAsInteger,
      positionId: parseAsInteger,
      includeDeleted: parseAsBoolean.withDefault(false),
    },
    {
      urlKeys: {
        companyId: 'company',
        positionId: 'position',
        includeDeleted: 'includeDeleted',
      },
    }
  )

  const setCompanyId = (value: number | undefined) => {
    // Positions are company-specific, so clear the position when the company changes.
    // Show deleted is only meaningful for a selected company.
    setFilters({
      companyId: value ?? null,
      positionId: null,
      ...(value === undefined ? { includeDeleted: false } : {}),
    })
    setPagination({ pageIndex: 0 })
  }

  const setPositionId = (value: number | undefined) => {
    setFilters({ positionId: value ?? null })
    setPagination({ pageIndex: 0 })
  }

  const setIncludeDeleted = (value: boolean) => {
    if (filters.companyId === null) {
      return
    }
    setFilters({ includeDeleted: value })
    setPagination({ pageIndex: 0 })
  }

  const includeDeleted = filters.companyId !== null && filters.includeDeleted

  const {
    data,
    isPending: isBillRatesLoading,
    isError: isBillRatesError,
    error: billRatesError,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useBillRatesQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    companyId: filters.companyId ?? undefined,
    positionId: filters.positionId ?? undefined,
    includeDeleted,
  })

  if (isBillRatesLoading) {
    return <Loading />
  }

  if (isBillRatesError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError: isBillRatesError,
      error: parseErrorMessage(billRatesError),
      companyId: filters.companyId,
      positionId: filters.positionId,
      includeDeleted,
      pagination,
    },
    actions: {
      refetch,
      setPagination,
      setCompanyId,
      setPositionId,
      setIncludeDeleted,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useBillRatesContextValue }
